import { useEffect, useRef, useState } from "react";

const PIXELS_PER_SECOND = 24;
// After a touch, the list waits this long before it moves again, so the reader can finish
const RESUME_AFTER_TOUCH_MS = 4000;

const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Scrolls a list slowly upwards, in a loop, when it is taller than its scroll container. The
 * caller renders the items twice: `copyRef` on the first copy and `nextRef` on the second, which
 * only shows while `looping` is true. When the first copy has scrolled out, the position jumps
 * back by one copy, which looks the same, so the list never ends.
 *
 * <p>It stops while the pointer is over the list, a control in it has focus, or a finger is on
 * it, and never moves for readers who ask the system for less motion.
 */
export const useAutoScroll = (itemCount) => {
  const containerRef = useRef(null);
  const copyRef = useRef(null);
  const nextRef = useRef(null);
  const [looping, setLooping] = useState(false);

  // Loop only when one copy of the list does not fit
  useEffect(() => {
    const container = containerRef.current;
    const copy = copyRef.current;
    if (!container || !copy || prefersReducedMotion()) return undefined;
    const measure = () => setLooping(copy.offsetHeight > container.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(copy);
    return () => observer.disconnect();
  }, [itemCount]);

  useEffect(() => {
    const container = containerRef.current;
    if (!looping || !container) return undefined;

    let paused = false;
    let resumeTimer;
    let frame;
    let last;
    // Kept as a float: scrollTop rounds, so adding a fraction of a pixel to it would never move
    let position = container.scrollTop;

    const pause = () => {
      clearTimeout(resumeTimer);
      paused = true;
    };
    const resume = () => {
      if (!container.matches(":hover") && !container.contains(document.activeElement)) paused = false;
    };
    const resumeLater = () => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(resume, RESUME_AFTER_TOUCH_MS);
    };

    const step = (now) => {
      const elapsed = last === undefined ? 0 : now - last;
      last = now;
      if (!paused && nextRef.current) {
        // The reader may have scrolled by hand while it was paused
        if (Math.abs(container.scrollTop - position) > 2) position = container.scrollTop;
        const period = nextRef.current.offsetTop - copyRef.current.offsetTop;
        position += (PIXELS_PER_SECOND * elapsed) / 1000;
        if (position >= period) position -= period;
        container.scrollTop = position;
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);

    const listeners = [
      ["mouseenter", pause],
      ["mouseleave", resume],
      ["focusin", pause],
      ["focusout", () => setTimeout(resume)],
      ["touchstart", pause],
      ["touchend", resumeLater],
    ];
    listeners.forEach(([type, handler]) => container.addEventListener(type, handler, { passive: true }));
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(resumeTimer);
      listeners.forEach(([type, handler]) => container.removeEventListener(type, handler));
    };
  }, [looping]);

  return { containerRef, copyRef, nextRef, looping };
};
